import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Cloud, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useCurrentAdmin } from '../../lib/useCurrentAdmin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// CloudinaryUsageCard — surfaces this month's storage / bandwidth /
// transformations / credit consumption so admins can spot a bandwidth spike
// before the free-tier limit is hit and image delivery degrades on the live
// site. Free tier caps at 25 credits/month (Cloudinary's own metric).
// ---------------------------------------------------------------------------
const fmt = (n, digits = 2) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: digits });

const ProgressBar = ({ percent, color, testid }) => {
  const pct = Math.max(0, Math.min(100, percent || 0));
  return (
    <div
      className="h-2 w-full rounded-full overflow-hidden"
      style={{ backgroundColor: 'rgba(0,0,0,0.06)' }}
      data-testid={testid}
    >
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
};

const CloudinaryUsageCard = () => {
  const { isAdmin } = useCurrentAdmin();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/cloudinary-usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.ok) {
        setData(res.data);
      } else {
        setError(res.data?.error || 'Cloudinary usage fetch failed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchUsage();
  }, [isAdmin]);

  if (!isAdmin) return null;

  // Threshold-based color for the credits bar. Free tier = 25 credits/month.
  const creditsPct = data?.credits?.used_percent ?? 0;
  const creditsColor = creditsPct >= 90 ? '#dc2626' : creditsPct >= 70 ? '#d97706' : '#16a34a';

  return (
    <Card className="mt-6" data-testid="admin-cloudinary-usage-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg flex items-center gap-2">
          <Cloud className="h-5 w-5" style={{ color: '#00CED1' }} />
          Cloudinary usage — this month
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchUsage}
          disabled={loading}
          data-testid="admin-cloudinary-usage-refresh"
          className="text-gray-600 hover:text-gray-900"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && (
          <p className="text-sm text-gray-500" data-testid="admin-cloudinary-usage-loading">
            Loading usage…
          </p>
        )}

        {error && (
          <div
            className="border rounded-md p-3 text-xs flex items-start gap-2"
            style={{ borderColor: '#dc2626', backgroundColor: 'rgba(220,38,38,0.05)' }}
            data-testid="admin-cloudinary-usage-error"
          >
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-red-700">Could not load Cloudinary usage</p>
              <p className="text-red-600 mt-1 break-words">{error}</p>
            </div>
          </div>
        )}

        {data && (
          <>
            {/* Plan + last-updated header */}
            <div className="flex items-center justify-between text-xs text-gray-500 flex-wrap gap-2">
              <span data-testid="admin-cloudinary-plan">
                Plan: <span className="font-semibold text-gray-800 uppercase">{data.plan || '—'}</span>
              </span>
              <span data-testid="admin-cloudinary-last-updated">
                Last updated: {data.last_updated || '—'}
              </span>
            </div>

            {/* Credits — most important: links to free-tier limit */}
            <div data-testid="admin-cloudinary-credits-row">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="font-semibold text-gray-800">Credits</span>
                <span className="text-gray-700 font-mono" data-testid="admin-cloudinary-credits-value">
                  {fmt(data.credits.used, 2)} / {fmt(data.credits.limit, 0)}
                  <span className="ml-2 text-xs" style={{ color: creditsColor }}>
                    ({fmt(creditsPct, 1)}%)
                  </span>
                </span>
              </div>
              <ProgressBar
                percent={creditsPct}
                color={creditsColor}
                testid="admin-cloudinary-credits-bar"
              />
              {creditsPct >= 70 && (
                <p
                  className="text-xs mt-1.5 flex items-center gap-1"
                  style={{ color: creditsColor }}
                  data-testid="admin-cloudinary-credits-warning"
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {creditsPct >= 90
                    ? 'Critical — credits nearly exhausted. Image delivery may degrade.'
                    : 'Approaching the monthly credit limit. Consider upgrading or auditing usage.'}
                </p>
              )}
            </div>

            {/* Storage / bandwidth / transformations breakdown */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              <UsageMetric
                label="Storage"
                primary={data.storage.bytes_human}
                secondary={`${fmt(data.storage.credits, 2)} credits`}
                testid="admin-cloudinary-storage"
              />
              <UsageMetric
                label="Bandwidth"
                primary={data.bandwidth.bytes_human}
                secondary={`${fmt(data.bandwidth.credits, 2)} credits`}
                testid="admin-cloudinary-bandwidth"
              />
              <UsageMetric
                label="Transformations"
                primary={fmt(data.transformations.count, 0)}
                secondary={`${fmt(data.transformations.credits, 2)} credits`}
                testid="admin-cloudinary-transformations"
              />
            </div>

            {/* Footer counts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-gray-100">
              <FooterStat label="Resources" value={fmt(data.resources, 0)} testid="admin-cloudinary-resources" />
              <FooterStat label="Derived" value={fmt(data.derived_resources, 0)} testid="admin-cloudinary-derived" />
              <FooterStat label="Objects" value={fmt(data.objects, 0)} testid="admin-cloudinary-objects" />
              <FooterStat label="Requests" value={fmt(data.requests, 0)} testid="admin-cloudinary-requests" />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const UsageMetric = ({ label, primary, secondary, testid }) => (
  <div className="border rounded-md p-3 bg-gray-50" data-testid={testid}>
    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</p>
    <p className="text-lg font-bold text-gray-900">{primary}</p>
    <p className="text-xs text-gray-500 mt-0.5">{secondary}</p>
  </div>
);

const FooterStat = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-gray-800 font-mono">{value}</p>
  </div>
);

export default CloudinaryUsageCard;
